import React, { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { oneformBFFNdpsSend } from 'src/bff';
import { GenericErrorLayer, Loading } from 'src/components';
import {
	FormController,
	getTrackingForm,
	LayoutWrapper,
	removeWhiteSpace,
	useOneFormContext,
	useTrackingManager,
} from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { DpsData } from 'src/feature-service/es-oneform-core-feature-service/types';
import {
	FormDataGroup,
	FormInfo,
	IdPerson,
	OneFormState,
	Steps,
} from 'src/types';
import { formDataBuilder } from '../format-fields';
import { BajaComunicacionesScreen } from './BajaComunicacionesScreen';
import { formDataAlta } from '../AltaComunicaciones';

interface BajaComunicacionesProps {
	idPerson: IdPerson;
	dpsData: DpsData;
}

export const BajaComunicacionesForm = (props: BajaComunicacionesProps) => {
	const { initializeForm } = useOneFormContext();
	const { idPerson, dpsData } = props;
	const store = useStore();
	const dispatch = useDispatch();
	const trackingManager = useTrackingManager();
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(true);

	const sendAltaForm = async () => {
		const result = await sendForm(true);
		trackingManager.trackFormStepButtonClick({
			contentId: 'Alta',
		});
		return result;
	};

	useEffect(() => {
		sendForm();
	}, []);

	const steps: Steps[] = [
		{
			title: null,
			fields: (
				<LayoutWrapper>
					<BajaComunicacionesScreen
						idPerson={idPerson}
						sendAltaForm={sendAltaForm}
					/>
				</LayoutWrapper>
			),
			screenIndex: 0,
			name: 'Form',
		},
	];

	const formDataBaja: Partial<FormDataGroup> = {
		headers: {
			DPS_TOKEN: 'VW_DDB_FRM_BAJA_COMUNICACIONES',
			FORM_OBJECTIVE: 'Baja Comunicaciones',
			FORM_NAME: 'vw-oneform-baja_comunicaciones',
		},
		fields: {
			formName: 'baja_comunicaciones',
			formType: 'otherform',
			pageCategory: 'Baja Comunicaciones',
		}, // Analítica
	};

	const getFormInfo = (): {
		firstStep: Steps;
		lastStep: Steps;
		stepsHistory: number[];
		numberOfScreens: number;
		newSteps?: Steps[];
	} => {
		return {
			firstStep: steps[0],
			lastStep: steps[steps.length - 1],
			stepsHistory: [0],
			numberOfScreens: steps.length,
			newSteps: null,
		};
	};

	const sendForm = (isAlta?: boolean): Promise<boolean> => {
		const { formData }: OneFormState = store.getState();
		const { fields } = formData;

		const personEmail =
			idPerson && idPerson.PersonEmail
				? idPerson.PersonEmail
				: removeWhiteSpace(fields.email);

		const personPhoneMobile =
			idPerson && idPerson.PersonMobilePhone
				? idPerson.PersonMobilePhone
				: removeWhiteSpace(fields.cellphone);

		if (!personEmail && !personPhoneMobile) {
			setError(true);
			setLoading(false);
			return;
		}

		return new Promise<any>(async (resolve, reject) => {
			const { tc, today, url, referalUrl, device } = getParamsForm();

			const data = {
				'headers[DPS_TOKEN]': isAlta
					? formDataAlta.headers.DPS_TOKEN
					: formDataBaja.headers.DPS_TOKEN,
				'headers[FORM_OBJECTIVE]': isAlta
					? formDataAlta.headers.FORM_OBJECTIVE
					: formDataBaja.headers.FORM_OBJECTIVE,
				'headers[FORM_NAME]': isAlta
					? formDataAlta.headers.FORM_NAME
					: formDataBaja.headers.FORM_NAME,
				'headers[FORM_VERSION]': isAlta
					? `${formDataAlta.headers.FORM_NAME}-${process.env.VERSION}`
					: `${formDataBaja.headers.FORM_NAME}-${process.env.VERSION}`,
				'headers[FORM_REFERER]': url,
				'headers[FORM_DATE_INSERTION]': today,
				'headers[ORIGIN_TRACKING_CODE]': tc,
				'headers[ORIGIN_DEVICE_TYPE]': device,
				'headers[FK_SYSTEM]':
					idPerson && idPerson.RCY_ExternalAccountID__c
						? idPerson.RCY_ExternalAccountID__c
						: '',

				'fields[DPS_BRAND]': 'Volkswagen',
				'fields[PERSON_EMAIL]': personEmail,
				'fields[PERSON_PHONE_MOBILE]': personPhoneMobile,
				'fields[COMMENTS_PERSON]': fields.masInformacion,
				'fields[PERSON_NAME]':
					idPerson && idPerson.FirstName
						? idPerson.FirstName
						: fields.name,
				'fields[PERSON_SURNAME_1]':
					idPerson && idPerson.LastName
						? idPerson.LastName
						: fields.surname,
				'fields[PERSON_SURNAME_2]':
					idPerson && idPerson.MiddleName
						? idPerson.MiddleName
						: fields.secondSurname,
				'fields[PERSON_RGPD_COM_COMERCIALES]': isAlta
					? 'PERMITE'
					: 'NO PERMITE',
				'fields[PERSON_RGPD_PERFILADO]': fields.tmk
					? fields.tmk
					: 'NO INFORMADO',

				'flags[PERSON_RGPD_ADAPTED]': 'true',
				'flags[FLAG_RELACION_MARCA_SIN_IC]': 'true',
			};
			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formData = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formData);

			setLoading(false);

			if (response?.data?.status?.code == 200) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				dispatch({ type: 'SET_FORM_SUBMITTED', payload: true });

				trackingManager.trackFormSubmissionSuccessMessageLoad(
					{
						contentId: undefined,
					},
					getTrackingForm(response.data),
				);
				setError(false);
				resolve(true);
			} else {
				setError(true);
				resolve(false);
			}
		});
	};

	useEffect(() => {
		const { firstStep, lastStep, stepsHistory, numberOfScreens, newSteps } =
			getFormInfo();

		const NuevoFormInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens,
			formTheme: 'main',
			screenIndex: firstStep.screenIndex,
			step: firstStep,
			steps: newSteps ? newSteps : steps,
			stepsHistory,
			notALayer: true,
			formType: 'inPage',
			alingItemsCenter: true,
			thankYouComponent: null,
		};
		initializeForm(NuevoFormInfo, formDataBaja);
	}, []);

	return loading || !steps ? (
		<Loading />
	) : error ? (
		<>
			<GenericErrorLayer
				title="Uy, algo ha pasado"
				bodycopy="Por alguna razón no hemos podido realizar la petición."
				notShowCTA={true}
				icon={false}
				notALayer={true}
			/>
		</>
	) : (
		<FormController steps={steps} screenType="full-screen" />
	);
};
